import { useState, useCallback, useRef } from "react";
import { Edit3, Upload, Download, Trash2, FileText, Loader2, Plus, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF editor free?",
    answer: "Yes, completely free with no signup. Edit unlimited PDFs online."
  },
  {
    question: "What kind of edits can I make?",
    answer: "You can add new text anywhere on the page with custom font size and color. Position text precisely using coordinates."
  },
  {
    question: "Can I edit existing text in the PDF?",
    answer: "Our tool focuses on adding new text. For complex text replacement, consider professional PDF editing software."
  },
  {
    question: "Will editing affect the PDF quality?",
    answer: "No, your original content remains intact. We only add new elements without affecting existing quality."
  },
  {
    question: "Are my documents secure?",
    answer: "Yes, all files are automatically deleted after 30 minutes. We never store or access your documents."
  }
];

const PdfEditPage = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [textBlocks, setTextBlocks] = useState([]);
  const [edits, setEdits] = useState([]);
  const [newText, setNewText] = useState("");
  const [newTextPosition, setNewTextPosition] = useState({ x: 100, y: 100 });
  const [fontSize, setFontSize] = useState([12]);
  const [fontColor, setFontColor] = useState("#000000");
  const [editMode, setEditMode] = useState("add"); // "add" or "replace"
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [replacementText, setReplacementText] = useState("");

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      await loadPdfData(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      await loadPdfData(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const loadPdfData = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      // Get preview
      const previewRes = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (previewRes.data.success) {
        setPages(previewRes.data.pages);
        setSelectedPage(1);
      }

      // Get text blocks
      const textFormData = new FormData();
      textFormData.append("file", pdfFile);
      const textRes = await axios.post(`${API}/pdf-get-text`, textFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (textRes.data.success) {
        setTextBlocks(textRes.data.pages);
      }
    } catch (error) {
      toast.error("Failed to load PDF");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const addTextEdit = () => {
    if (!newText.trim()) {
      toast.error("Please enter text");
      return;
    }

    const edit = {
      page: selectedPage,
      x: newTextPosition.x,
      y: newTextPosition.y,
      text: newText,
      font_size: fontSize[0],
      font_color: fontColor.replace("#", ""),
      action: "add"
    };

    setEdits([...edits, edit]);
    setNewText("");
    toast.success("Text added to queue");
  };

  const addReplaceEdit = () => {
    if (!selectedBlock || !replacementText.trim()) {
      toast.error("Please select text to replace and enter replacement");
      return;
    }

    const edit = {
      page: selectedPage,
      x: selectedBlock.x,
      y: selectedBlock.y,
      text: replacementText,
      font_size: fontSize[0],
      font_color: fontColor.replace("#", ""),
      action: "replace",
      original_text: selectedBlock.text
    };

    setEdits([...edits, edit]);
    setReplacementText("");
    setSelectedBlock(null);
    toast.success("Replacement added to queue");
  };

  const removeEdit = (index) => {
    setEdits(edits.filter((_, i) => i !== index));
  };

  const handleApplyEdits = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (edits.length === 0) {
      toast.error("No edits to apply");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("edits", JSON.stringify(edits));

    try {
      const response = await axios.post(`${API}/pdf-edit-text`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Edit failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "edited.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPages([]);
    setTextBlocks([]);
    setEdits([]);
    setNewText("");
    setSelectedBlock(null);
  };

  const currentPageBlocks = textBlocks.find(p => p.page === selectedPage)?.text_blocks || [];

  return (
    <>
      <SEOHead
        title="Edit PDF Online Free | Add Text to PDF"
        description="Edit PDF files online free. Add text, customize fonts and colors. Free online PDF editor. No signup required."
        canonicalPath="/pdf-edit"
        keywords="edit PDF, PDF editor free, add text to PDF, modify PDF online, edit PDF text free"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Edit3 className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Edit PDF Online Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Add text to your PDF documents with custom fonts, sizes, and colors. 
              Position text precisely where you need it. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!result && pages.length === 0 && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {isLoadingPreview ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Loading PDF...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Works best with text-based PDFs (not scanned)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Edit Interface */}
        {!result && pages.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in-up">
            {/* Left: PDF Preview */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Page {selectedPage} of {pages.length}</h3>
              </div>
              <div className="border border-border bg-secondary overflow-auto max-h-[600px]">
                <img
                  src={`${API}/download/${pages[selectedPage - 1]?.thumbnail}`}
                  alt={`Page ${selectedPage}`}
                  className="w-full h-auto"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {pages.map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPage(idx + 1)}
                    className={`w-12 h-16 border-2 overflow-hidden ${
                      selectedPage === idx + 1 ? "border-[#FF3B30]" : "border-border"
                    }`}
                  >
                    <img
                      src={`${API}/download/${page.thumbnail}`}
                      alt={`Page ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Edit Controls */}
            <div className="space-y-6">
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <Button
                  variant={editMode === "add" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditMode("add")}
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Text
                </Button>
                <Button
                  variant={editMode === "replace" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditMode("replace")}
                  className="flex-1"
                >
                  <Type className="w-4 h-4 mr-1" /> Replace
                </Button>
              </div>

              {/* Add New Text */}
              {editMode === "add" && (
                <div className="border border-border p-4 space-y-4">
                  <h4 className="font-medium">Add New Text</h4>
                  <Input
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Enter text..."
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>X Position</Label>
                      <Input
                        type="number"
                        value={newTextPosition.x}
                        onChange={(e) => setNewTextPosition(p => ({ ...p, x: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label>Y Position</Label>
                      <Input
                        type="number"
                        value={newTextPosition.y}
                        onChange={(e) => setNewTextPosition(p => ({ ...p, y: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Font Size: {fontSize[0]}px</Label>
                    <Slider value={fontSize} onValueChange={setFontSize} min={8} max={72} />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <input
                      type="color"
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                      className="w-full h-10 cursor-pointer"
                    />
                  </div>
                  <Button onClick={addTextEdit} className="w-full">
                    <Plus className="w-4 h-4 mr-1" /> Add to Queue
                  </Button>
                </div>
              )}

              {/* Replace Text */}
              {editMode === "replace" && (
                <div className="border border-border p-4 space-y-4">
                  <h4 className="font-medium">Replace Existing Text</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {currentPageBlocks.slice(0, 20).map((block, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedBlock(block)}
                        className={`w-full text-left p-2 text-sm truncate ${
                          selectedBlock?.text === block.text
                            ? "bg-[#FF3B30] text-white"
                            : "bg-secondary hover:bg-secondary/80"
                        }`}
                      >
                        {block.text}
                      </button>
                    ))}
                    {currentPageBlocks.length === 0 && (
                      <p className="text-sm text-muted-foreground">No text found on this page</p>
                    )}
                  </div>
                  {selectedBlock && (
                    <>
                      <div className="p-2 bg-secondary">
                        <p className="text-xs text-muted-foreground">Selected:</p>
                        <p className="text-sm font-medium">{selectedBlock.text}</p>
                      </div>
                      <Input
                        value={replacementText}
                        onChange={(e) => setReplacementText(e.target.value)}
                        placeholder="New text..."
                      />
                      <Button onClick={addReplaceEdit} className="w-full">
                        Add Replacement
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Edit Queue */}
              {edits.length > 0 && (
                <div className="border border-border p-4 space-y-2">
                  <h4 className="font-medium">Pending Edits ({edits.length})</h4>
                  {edits.map((edit, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-secondary text-sm">
                      <span className="truncate flex-1">
                        {edit.action === "add" ? "Add" : "Replace"}: {edit.text}
                      </span>
                      <button
                        onClick={() => removeEdit(idx)}
                        className="text-[#FF3B30] hover:underline ml-2"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Apply Button */}
              <div className="flex gap-4">
                <Button variant="outline" onClick={handleReset} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleApplyEdits}
                  disabled={edits.length === 0 || isProcessing}
                  className="flex-1 bg-[#FF3B30] hover:bg-[#FF3B30]/90"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Apply {edits.length} Edit{edits.length !== 1 ? "s" : ""}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <Edit3 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Edited Successfully
              </h2>
              <p className="text-muted-foreground mb-6">{result.message}</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={handleReset} className="rounded-sm">
                  <Trash2 className="w-4 h-4 mr-2" /> Edit Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-edit" content={toolContent["pdf-edit"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-edit" />
      </div>
    </div>
    </>
  );
};

export default PdfEditPage;
